import { generateAccountKeys, openAccountKeys, type AccountKeyRecord, type AccountKeys, type MasterKey } from '@realmery/vault';
import { AccountError, api } from './api.ts';

/**
 * The account's sharing keys (docs/SHARING-PLAN.md section 3.2): made in this browser the
 * first time the account is unlocked, kept on the server with the private halves sealed,
 * and opened on every device after. One attempt at a time; a failed one (offline, say) is
 * tried again at the next unlock or visit to the account page.
 */

let current: { userId: string; master: MasterKey; keys: Promise<AccountKeys> } | null = null;

export function sharingKeys(userId: string, master: MasterKey): Promise<AccountKeys> {
  if (current && current.userId === userId && current.master === master) return current.keys;
  const keys = load(userId, master);
  current = { userId, master, keys };
  // Forget a failure, so the next call tries again.
  keys.catch(() => {
    if (current?.keys === keys) current = null;
  });
  return keys;
}

async function load(userId: string, master: MasterKey): Promise<AccountKeys> {
  const stored = await api.get<{ keys: (AccountKeyRecord & { createdAt: number }) | null }>('account/keys');
  if (stored.keys) return openAccountKeys(master, userId, stored.keys);
  const made = await generateAccountKeys(master, userId);
  try {
    await api.post('account/keys', made.record);
    return made;
  } catch (error) {
    // Another device or tab made them first: theirs are kept, so use them.
    if (error instanceof AccountError && error.code === 'keys_exist') {
      const again = await api.get<{ keys: AccountKeyRecord | null }>('account/keys');
      if (again.keys) return openAccountKeys(master, userId, again.keys);
    }
    throw error;
  }
}

/** Forgets the opened keys, on locking or signing out. */
export function forgetSharingKeys(): void {
  current = null;
}
